import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Mail, Phone, MapPin, ExternalLink, Play, Send, Check,
  Facebook, Instagram, Youtube, Twitter, Linkedin, Globe
} from 'lucide-react';
import { FaWhatsapp, FaTelegram, FaTiktok, FaPinterest } from 'react-icons/fa';
import { toast } from 'sonner';
import api from '../utils/api';
import { getImageUrl } from '../utils/imageUrl';

const SOCIAL_ICONS = {
  whatsapp: FaWhatsapp,
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
  twitter: Twitter,
  linkedin: Linkedin,
  telegram: FaTelegram,
  tiktok: FaTiktok,
  pinterest: FaPinterest,
  website: Globe
};

const SOCIAL_COLORS = {
  whatsapp: '#25D366',
  facebook: '#1877F2',
  instagram: '#E4405F',
  youtube: '#FF0000',
  twitter: '#1DA1F2',
  linkedin: '#0A66C2',
  telegram: '#0088cc',
  tiktok: '#000000',
  pinterest: '#E60023',
  website: '#6366F1'
};

const PublicPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    try {
      const res = await api.get(`/pages/view/${slug}`);
      setPage(res.data);
      
      // Initialize form data
      if (res.data.contact_form_fields) {
        const initialData = {};
        res.data.contact_form_fields.forEach(field => {
          initialData[field.name] = '';
        });
        setFormData(initialData);
      }
    } catch (err) {
      setError('Page not found');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    try {
      await api.post(`/pages/contact/${slug}`, formData);
      setFormSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setFormSubmitting(false);
    }
  };

  const getWhatsAppLink = () => {
    if (!page?.whatsapp_number) return null;
    const message = encodeURIComponent(page.whatsapp_message || 'Hello!');
    return `https://wa.me/${page.whatsapp_number}?text=${message}`;
  };

  const getVideoEmbedUrl = (url) => {
    if (!url) return null;
    if (url.includes('youtube.com/watch')) {
      return url.replace('watch?v=', 'embed/');
    }
    if (url.includes('youtu.be/')) {
      return url.replace('youtu.be/', 'youtube.com/embed/');
    }
    if (url.includes('vimeo.com/')) {
      const id = url.split('/').pop();
      return `https://player.vimeo.com/video/${id}`;
    }
    return url;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">404</h1>
          <p className="text-muted-foreground">Page not found</p>
        </div>
      </div>
    );
  }

  const styles = {
    '--primary': page.primary_color || '#D4AF37',
    '--secondary': page.secondary_color || '#1a1a2e',
    '--bg': page.background_color || '#0a0a0a',
    '--text': page.text_color || '#ffffff',
    fontFamily: page.font_family || 'Inter'
  };

  // Render based on template
  const renderTemplate = () => {
    switch (page.template) {
      case 'business':
        return <BusinessTemplate page={page} styles={styles} getWhatsAppLink={getWhatsAppLink} />;
      case 'portfolio':
        return <PortfolioTemplate page={page} styles={styles} setLightboxImage={setLightboxImage} />;
      case 'minimal':
        return <MinimalTemplate page={page} styles={styles} getWhatsAppLink={getWhatsAppLink} />;
      case 'luxury':
      case 'landing':
      default:
        return (
          <LuxuryTemplate 
            page={page} 
            styles={styles} 
            getWhatsAppLink={getWhatsAppLink}
            getVideoEmbedUrl={getVideoEmbedUrl}
            formData={formData}
            setFormData={setFormData}
            handleFormSubmit={handleFormSubmit}
            formSubmitting={formSubmitting}
            formSubmitted={formSubmitted}
            setLightboxImage={setLightboxImage}
          />
        );
    }
  };

  return (
    <div style={styles}>
      {renderTemplate()}
      
      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <img src={lightboxImage} alt="" className="max-w-full max-h-full object-contain" />
        </div>
      )}

      {/* Branding */}
      {page.show_branding && (
        <div className="fixed bottom-4 right-4 px-3 py-1.5 bg-black/80 backdrop-blur rounded-full text-xs text-white/60">
          Powered by <span className="text-primary font-semibold">ANANT</span>
        </div>
      )}
    </div>
  );
};

// LUXURY TEMPLATE - Premium Gold Theme
const LuxuryTemplate = ({ page, styles, getWhatsAppLink, getVideoEmbedUrl, formData, setFormData, handleFormSubmit, formSubmitting, formSubmitted, setLightboxImage }) => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}>
      {/* Hero Section */}
      <div className="relative">
        {page.cover_image ? (
          <div 
            className="h-80 bg-cover bg-center"
            style={{ backgroundImage: `linear-gradient(to bottom, transparent 50%, var(--bg)), url(${getImageUrl(page.cover_image)})` }}
          />
        ) : (
          <div className="h-80 bg-gradient-to-br from-primary/30 via-transparent to-secondary/30" />
        )}
        
        {/* Profile */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 text-center">
          {page.profile_image ? (
            <img 
              src={getImageUrl(page.profile_image)} 
              alt="" 
              className="w-32 h-32 rounded-full border-4 object-cover mx-auto shadow-2xl"
              style={{ borderColor: 'var(--primary)' }}
            />
          ) : page.logo_url ? (
            <img src={getImageUrl(page.logo_url)} alt="" className="h-24 mx-auto" />
          ) : null}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 pt-20 pb-24">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-3" style={{ color: 'var(--primary)' }}>
            {page.headline || page.title}
          </h1>
          {page.tagline && (
            <p className="text-xl opacity-80">{page.tagline}</p>
          )}
          {page.description && (
            <p className="mt-4 max-w-2xl mx-auto opacity-70">{page.description}</p>
          )}
        </div>

        {/* WhatsApp CTA */}
        {getWhatsAppLink() && (
          <div className="mb-12">
            <a
              href={getWhatsAppLink()}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-3 py-4 px-8 rounded-2xl text-white text-lg font-semibold mx-auto max-w-md transition-transform hover:scale-105 shadow-xl"
              style={{ backgroundColor: '#25D366' }}
            >
              <FaWhatsapp className="w-7 h-7" />
              Chat on WhatsApp
            </a>
          </div>
        )}

        {/* Social Links */}
        {page.social_links?.length > 0 && (
          <div className="flex justify-center gap-4 mb-12 flex-wrap">
            {page.social_links.map((link, i) => {
              const Icon = SOCIAL_ICONS[link.platform] || Globe;
              const color = SOCIAL_COLORS[link.platform] || 'var(--primary)';
              return (
                <a
                  key={i}
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="w-14 h-14 rounded-2xl flex items-center justify-center transition-transform hover:scale-110 shadow-lg"
                  style={{ backgroundColor: color + '20', color: color }}
                >
                  <Icon className="w-6 h-6" />
                </a>
              );
            })}
          </div>
        )}

        {/* CTA Buttons */}
        {page.cta_buttons?.length > 0 && (
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {page.cta_buttons.map((btn, i) => (
              <a
                key={i}
                href={btn.url}
                target="_blank"
                rel="noreferrer"
                className={`px-8 py-3 rounded-xl font-semibold transition-transform hover:scale-105 flex items-center gap-2 ${
                  btn.style === 'primary' ? 'text-black shadow-lg' :
                  btn.style === 'secondary' ? 'bg-white/10' : 'border-2'
                }`}
                style={{ 
                  backgroundColor: btn.style === 'primary' ? 'var(--primary)' : undefined,
                  borderColor: btn.style === 'outline' ? 'var(--primary)' : undefined,
                  color: btn.style === 'outline' ? 'var(--primary)' : undefined
                }}
              >
                {btn.text}
                <ExternalLink className="w-4 h-4" />
              </a>
            ))}
          </div>
        )}

        {/* Text Sections */}
        {page.text_sections?.length > 0 && (
          <div className="space-y-8 mb-12">
            {page.text_sections.map((section, i) => (
              <div key={i} className="p-6 rounded-2xl" style={{ backgroundColor: 'var(--secondary)' }}>
                <h3 className="text-2xl font-bold mb-3" style={{ color: 'var(--primary)' }}>{section.title}</h3>
                <p className="opacity-80 whitespace-pre-wrap">{section.content}</p>
              </div>
            ))}
          </div>
        )}

        {/* Gallery */}
        {page.gallery_images?.length > 0 && (
          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: 'var(--primary)' }}>
              {page.gallery_title || 'Gallery'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {page.gallery_images.map((img, i) => (
                <div 
                  key={i} 
                  className="aspect-square rounded-xl overflow-hidden cursor-pointer transition-transform hover:scale-105 shadow-lg"
                  onClick={() => setLightboxImage(getImageUrl(img.url))}
                >
                  <img src={getImageUrl(img.url)} alt={img.caption || ''} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Video */}
        {page.video_url && (
          <div className="mb-12">
            {page.video_title && (
              <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: 'var(--primary)' }}>
                {page.video_title}
              </h3>
            )}
            <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
              <iframe
                src={getVideoEmbedUrl(page.video_url)}
                className="w-full h-full"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            </div>
          </div>
        )}

        {/* Contact Form */}
        {page.enable_contact_form && (
          <div className="p-8 rounded-2xl" style={{ backgroundColor: 'var(--secondary)' }}>
            <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: 'var(--primary)' }}>
              {page.contact_form_title || 'Contact Us'}
            </h3>
            
            {formSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'var(--primary)' }}>
                  <Check className="w-8 h-8 text-black" />
                </div>
                <h4 className="text-xl font-bold mb-2">Message Sent!</h4>
                <p className="opacity-70">We'll get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleFormSubmit} className="space-y-4 max-w-lg mx-auto">
                {page.contact_form_fields?.map((field, i) => (
                  <div key={i}>
                    <label className="block text-sm mb-1 capitalize opacity-70">
                      {field.name} {field.required && <span style={{ color: 'var(--primary)' }}>*</span>}
                    </label>
                    {field.field_type === 'textarea' ? (
                      <textarea
                        required={field.required}
                        value={formData[field.name] || ''}
                        onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 focus:border-primary outline-none"
                        rows={4}
                      />
                    ) : (
                      <input
                        type={field.field_type}
                        required={field.required}
                        value={formData[field.name] || ''}
                        onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 focus:border-primary outline-none"
                      />
                    )}
                  </div>
                ))}
                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 text-black transition-transform hover:scale-105"
                  style={{ backgroundColor: 'var(--primary)' }}
                >
                  {formSubmitting ? 'Sending...' : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        )}

        {/* Contact Info */}
        {(page.email || page.phone || page.address) && (
          <div className="mt-12 flex flex-wrap justify-center gap-6">
            {page.email && (
              <a href={`mailto:${page.email}`} className="flex items-center gap-2 opacity-70 hover:opacity-100">
                <Mail className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                {page.email}
              </a>
            )}
            {page.phone && (
              <a href={`tel:${page.phone}`} className="flex items-center gap-2 opacity-70 hover:opacity-100">
                <Phone className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                {page.phone}
              </a>
            )}
            {page.address && (
              <span className="flex items-center gap-2 opacity-70">
                <MapPin className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                {page.address}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// BUSINESS CARD TEMPLATE
const BusinessTemplate = ({ page, styles, getWhatsAppLink }) => {
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}>
      <div className="w-full max-w-md rounded-3xl overflow-hidden shadow-2xl" style={{ backgroundColor: 'var(--secondary)' }}>
        {/* Cover */}
        {page.cover_image && (
          <div 
            className="h-32 bg-cover bg-center"
            style={{ backgroundImage: `url(${getImageUrl(page.cover_image)})` }}
          />
        )}
        
        {/* Profile */}
        <div className={`text-center ${page.cover_image ? '-mt-12' : 'pt-8'} px-6`}>
          {page.profile_image && (
            <img 
              src={getImageUrl(page.profile_image)} 
              alt="" 
              className="w-24 h-24 rounded-full border-4 object-cover mx-auto"
              style={{ borderColor: 'var(--secondary)' }}
            />
          )}
          <h1 className="text-2xl font-bold mt-4" style={{ color: 'var(--primary)' }}>
            {page.headline || page.title}
          </h1>
          {page.tagline && <p className="opacity-70 mt-1">{page.tagline}</p>}
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* WhatsApp */}
          {getWhatsAppLink() && (
            <a
              href={getWhatsAppLink()}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 py-3 rounded-xl text-white font-semibold"
              style={{ backgroundColor: '#25D366' }}
            >
              <FaWhatsapp className="w-5 h-5" />
              WhatsApp
            </a>
          )}

          {/* Social Links */}
          {page.social_links?.length > 0 && (
            <div className="flex justify-center gap-3 flex-wrap">
              {page.social_links.map((link, i) => {
                const Icon = SOCIAL_ICONS[link.platform] || Globe;
                const color = SOCIAL_COLORS[link.platform];
                return (
                  <a
                    key={i}
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: color + '20', color: color }}
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          )}

          {/* Contact Info */}
          <div className="space-y-2 pt-4 border-t border-white/10">
            {page.email && (
              <a href={`mailto:${page.email}`} className="flex items-center gap-3 opacity-70 hover:opacity-100">
                <Mail className="w-4 h-4" style={{ color: 'var(--primary)' }} />
                <span className="text-sm">{page.email}</span>
              </a>
            )}
            {page.phone && (
              <a href={`tel:${page.phone}`} className="flex items-center gap-3 opacity-70 hover:opacity-100">
                <Phone className="w-4 h-4" style={{ color: 'var(--primary)' }} />
                <span className="text-sm">{page.phone}</span>
              </a>
            )}
            {page.address && (
              <div className="flex items-start gap-3 opacity-70">
                <MapPin className="w-4 h-4 mt-0.5" style={{ color: 'var(--primary)' }} />
                <span className="text-sm">{page.address}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// PORTFOLIO TEMPLATE
const PortfolioTemplate = ({ page, styles, setLightboxImage }) => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}>
      {/* Header */}
      <div className="py-12 text-center">
        {page.profile_image && (
          <img src={getImageUrl(page.profile_image)} alt="" className="w-20 h-20 rounded-full mx-auto mb-4 object-cover" />
        )}
        <h1 className="text-3xl font-bold" style={{ color: 'var(--primary)' }}>{page.headline || page.title}</h1>
        {page.tagline && <p className="opacity-70 mt-2">{page.tagline}</p>}
        
        {/* Social Links */}
        {page.social_links?.length > 0 && (
          <div className="flex justify-center gap-3 mt-6">
            {page.social_links.map((link, i) => {
              const Icon = SOCIAL_ICONS[link.platform] || Globe;
              return (
                <a key={i} href={link.url} target="_blank" rel="noreferrer" className="opacity-60 hover:opacity-100">
                  <Icon className="w-5 h-5" />
                </a>
              );
            })}
          </div>
        )}
      </div>

      {/* Gallery Grid */}
      <div className="max-w-6xl mx-auto px-4 pb-12">
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4">
          {page.gallery_images?.map((img, i) => (
            <div 
              key={i} 
              className="break-inside-avoid mb-4 cursor-pointer overflow-hidden rounded-lg"
              onClick={() => setLightboxImage(getImageUrl(img.url))}
            >
              <img src={getImageUrl(img.url)} alt={img.caption || ''} className="w-full hover:scale-105 transition-transform" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// MINIMAL TEMPLATE
const MinimalTemplate = ({ page, styles, getWhatsAppLink }) => {
  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}>
      <div className="max-w-xl w-full text-center">
        {page.profile_image && (
          <img src={getImageUrl(page.profile_image)} alt="" className="w-28 h-28 rounded-full mx-auto mb-6 object-cover" />
        )}
        
        <h1 className="text-4xl font-bold mb-2">{page.headline || page.title}</h1>
        {page.tagline && <p className="text-xl opacity-60 mb-6">{page.tagline}</p>}
        {page.description && <p className="opacity-70 mb-8">{page.description}</p>}

        {/* Links */}
        <div className="space-y-3">
          {getWhatsAppLink() && (
            <a
              href={getWhatsAppLink()}
              target="_blank"
              rel="noreferrer"
              className="block py-3 px-6 rounded-xl font-medium transition-transform hover:scale-105"
              style={{ backgroundColor: 'var(--primary)', color: 'var(--bg)' }}
            >
              WhatsApp
            </a>
          )}
          
          {page.social_links?.map((link, i) => {
            const platform = SOCIAL_ICONS[link.platform] ? link.platform : 'website';
            return (
              <a
                key={i}
                href={link.url}
                target="_blank"
                rel="noreferrer"
                className="block py-3 px-6 rounded-xl font-medium transition-all hover:bg-white/20"
                style={{ backgroundColor: 'white/5', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                {platform.charAt(0).toUpperCase() + platform.slice(1)}
              </a>
            );
          })}
        </div>

        {/* Contact */}
        <div className="mt-8 flex flex-wrap justify-center gap-4 opacity-60">
          {page.email && <span>{page.email}</span>}
          {page.phone && <span>{page.phone}</span>}
        </div>
      </div>
    </div>
  );
};

export default PublicPage;
